import apiService from '../../../services/api'
import toast from 'react-hot-toast'
import i18n from '../../../i18n'

/**
 * API банд (docs/team.md).
 *
 * Все ручки — прямым fetch мимо apiService.makeRequest, чтобы читать тела
 * ошибок (403 deposit_required, 409 name_taken, 429 retry_after и т.д.):
 * makeRequest на 4xx бросает generic-ошибку без разбора тела. Тот же приём,
 * что в features/profile/services/profileApi.js.
 *
 * @returns {Promise<{ok: boolean, status: number, data: any}>}
 */
async function request(path, { method = 'GET', body, signal } = {}) {
  let response, authContext
  try {
    ({ response, authContext } = await apiService.fetchWithAuth(path, {
      method,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal
    }))
  } catch (error) {
    if (error.name === 'AbortError') return { ok: false, status: 0, data: null, aborted: true }
    console.error('Ошибка запроса банды:', error)
    return { ok: false, status: 0, data: null }
  }

  let data = null
  try {
    data = await response.json()
  } catch (e) {
    // тело может быть пустым
  }

  if (apiService.getAuthContext().token !== authContext.token) return { ok: false, status: 0, data: null }
  if (response.status === 429) toast.error(i18n.t('api.rateLimited'), { id: 'team-rate-limited' })
  return { ok: response.ok, status: response.status, data }
}

/**
 * Multipart-запрос (аватар и фон банды). Content-Type не выставляем —
 * браузер сам проставит его с boundary для FormData.
 * 413 от nginx приходит HTML-страницей без JSON — тело не разбираем.
 * @returns {Promise<{ok: boolean, status: number, data: any}>}
 */
async function multipartRequest(path, formData) {
  let response
  try {
    ({ response } = await apiService.fetchWithAuth(path, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    }))
  } catch (error) {
    console.error('Ошибка запроса банды:', error)
    return { ok: false, status: 0, data: null }
  }

  let data = null
  if (response.status !== 413) {
    try {
      data = await response.json()
    } catch (e) {
      // тело может быть пустым
    }
  }

  return { ok: response.ok, status: response.status, data }
}

export const teamApi = {
  /**
   * Каталог банд (GET /api/team/). Без q — только набирающие банды,
   * с q (от 2 символов) — все живые, в том числе с recruiting: false.
   *
   * Фильтры (docs/team-api.md §2) необязательны и сочетаются и друг с другом,
   * и с поиском: level, modes (через запятую), age, activity, free, sort.
   * Пустое значение не отправляем вовсе — иначе бэкенд ответит invalid_filter.
   */
  getCatalog: ({ q, page, level, modes, age, activity, free, sort } = {}) => {
    const params = new URLSearchParams()
    if (q) params.set('q', q)
    if (page && page > 1) params.set('page', String(page))
    if (level) params.set('level', level)
    if (Array.isArray(modes) && modes.length > 0) params.set('modes', modes.join(','))
    if (age) params.set('age', age)
    if (activity) params.set('activity', activity)
    // free=0 равносильно отсутствию фильтра — не отправляем
    if (free) params.set('free', '1')
    if (sort && sort !== 'online') params.set('sort', sort)
    const query = params.toString()
    return request(`/api/team/${query ? `?${query}` : ''}`)
  },

  getCapture: () => request('/api/team/capture/'),

  getCaptureMode: (mode) => request(`/api/team/capture/${encodeURIComponent(mode)}/`),

  getTop: () => request('/api/team/top/'),

  getTopHistory: (mode) => request(`/api/team/top/history/${mode ? `?mode=${encodeURIComponent(mode)}` : ''}`),

  /** Карточка банды по slug (GET /api/team/{slug}/) */
  getTeam: (slug) => request(`/api/team/${slug}/`),

  /** Своя банда целиком (GET /api/team/my/); team: null — ни в какой не состоит */
  getMyTeam: () => request('/api/team/my/'),

  getChat: (afterId, signal) => request(`/api/team/chat/${afterId == null ? '' : `?after_id=${encodeURIComponent(afterId)}`}`, { signal }),

  sendChat: (text, clientId) => request('/api/team/chat/', { method: 'POST', body: { text, client_id: clientId } }),

  /** Создание банды (POST /api/team/create/). Ответ 200 — карточка новой банды */
  create: (name) => request('/api/team/create/', { method: 'POST', body: { name } }),

  /** Заявка на вступление (POST /api/team/{slug}/apply/), текст до 300 графем и необязателен */
  apply: (slug, text) => request(`/api/team/${slug}/apply/`, { method: 'POST', body: { text: text || '' } }),

  /** Мои заявки и их статусы (GET /api/team/applications/) */
  getMyApplications: () => request('/api/team/applications/'),

  /** Отозвать заявку; без id — сразу все (POST /api/team/applications/cancel/) */
  cancelApplication: (id) => request('/api/team/applications/cancel/', {
    method: 'POST',
    body: id != null ? { id } : {}
  }),

  /** Принять заявку (модератор и выше). Ответ 200 — свежая карточка своей банды */
  approveApplication: (id) => request(`/api/team/applications/${id}/approve/`, { method: 'POST' }),

  /** Отклонить заявку (модератор и выше). Ответ 200 — свежая карточка своей банды */
  rejectApplication: (id) => request(`/api/team/applications/${id}/reject/`, { method: 'POST' }),

  /**
   * Смена роли участника (POST /api/team/members/{chat_id}/role/).
   * role — deputy / moderator / member; owner выдаётся только передачей создателя.
   */
  setRole: (chatId, role) => request(`/api/team/members/${chatId}/role/`, { method: 'POST', body: { role } }),

  /** Исключить участника (POST /api/team/members/{chat_id}/kick/) */
  kick: (chatId) => request(`/api/team/members/${chatId}/kick/`, { method: 'POST' }),

  /** Выйти из банды (POST /api/team/leave/); разрешён и в заморозке, и при таймере удаления */
  leave: () => request('/api/team/leave/', { method: 'POST', body: { confirm: 1 } }),

  /** Передача создателя (POST /api/team/transfer/); согласия принимающего не спрашивают */
  transfer: (chatId) => request('/api/team/transfer/', { method: 'POST', body: { chat_id: chatId, confirm: 1 } }),

  /** Выплата из казны участнику (POST /api/team/treasury/pay/) — создатель и зам */
  pay: (chatId, amount) => request('/api/team/treasury/pay/', {
    method: 'POST',
    body: { chat_id: chatId, amount }
  }),

  /** Журнал казны (GET /api/team/treasury/history/) — любому участнику */
  getTreasuryHistory: (page = 1) => request(`/api/team/treasury/history/?page=${page}`),

  /**
   * Настройки банды (POST /api/team/settings/) — частичное обновление:
   * name, description, recruit_text, contact_chat, contact_channel,
   * show_members, show_treasury, show_contacts, show_stats, recruiting.
   * Ответ 200 — свежая карточка своей банды.
   */
  saveSettings: (payload) => request('/api/team/settings/', { method: 'POST', body: payload }),

  /** Объявление лидера (POST /api/team/announcement/) — только создатель */
  setAnnouncement: (text) => request('/api/team/announcement/', { method: 'POST', body: { text } }),

  /** Цвета банды (POST /api/team/colors/) — частичное обновление, как в профиле */
  saveColors: (colors) => request('/api/team/colors/', { method: 'POST', body: colors }),

  /** Аватар банды (POST /api/team/avatar/, multipart) — квадрат до 256×256 */
  uploadAvatar: (file) => {
    const formData = new FormData()
    formData.append('file', file.blob, file.name)
    return multipartRequest('/api/team/avatar/', formData)
  },

  /** Снятие аватара (POST /api/team/avatar/delete/) */
  deleteAvatar: () => request('/api/team/avatar/delete/', { method: 'POST' }),

  /** Фон карточки банды (POST /api/team/background/, multipart): файл + настройки отображения */
  uploadBackground: (file, settings) => {
    const formData = new FormData()
    formData.append('file', file.blob, file.name)
    if (settings) {
      Object.keys(settings).forEach((key) => {
        formData.append(key, settings[key])
      })
    }
    return multipartRequest('/api/team/background/', formData)
  },

  /**
   * Настройки фона без перезаливки картинки (POST /api/team/background/settings/):
   * fit / position_x / position_y / overlay / blur. Ручка для автосейва слайдеров.
   */
  saveBackgroundSettings: (settings) => request('/api/team/background/settings/', {
    method: 'POST',
    body: settings
  }),

  /** Снятие фона (POST /api/team/background/delete/); настройки отображения сбрасываются к дефолтам */
  deleteBackground: () => request('/api/team/background/delete/', { method: 'POST' }),

  /**
   * Prepared message «Пригласить в банду» для Telegram.WebApp.shareMessage
   * (POST /api/team/share/). Тела нет — бэкенд сам берёт банду игрока.
   * Показывать только по `can_share` в карточке.
   */
  share: () => request('/api/team/share/', { method: 'POST', body: {} }),

  /** Лайк / дизлайк / снятие голоса (POST /api/team/{slug}/like/): value 1 | -1 | 0 */
  setVote: (slug, value) => request(`/api/team/${slug}/like/`, { method: 'POST', body: { value } }),

  /**
   * Роспуск банды (POST /api/team/delete/) — только создатель. В ответе `disbanded`:
   * `true` — пустую банду распустили сразу (`team: null`), `false` — запущен таймер на 6 часов
   */
  deleteTeam: () => request('/api/team/delete/', { method: 'POST', body: { confirm: 1 } }),

  /** Отмена удаления (POST /api/team/delete/cancel/) — только создатель */
  cancelDelete: () => request('/api/team/delete/cancel/', { method: 'POST' }),

  /** Аудит-лог банды (GET /api/team/audit/) — любому участнику, 50 событий на страницу */
  getAudit: (page = 1) => request(`/api/team/audit/?page=${page}`),

  /**
   * Статистика командных игр (GET /api/team/{slug}/stats/).
   * period — 'today' | 'yesterday' | '7' | '30' | 'all'. Чужому при `show_stats: false` — 403 stats_hidden.
   */
  getStats: (slug, period = 'all') => request(`/api/team/${slug}/stats/?period=${period}`),

  /**
   * История командных игр (GET /api/team/{slug}/games/), 20 на страницу, свежие сверху.
   * kind — необязательный фильтр по режиму (те же 8 значений, что в modes).
   */
  getGames: (slug, { page = 1, kind } = {}) => {
    const params = new URLSearchParams()
    if (page && page > 1) params.set('page', String(page))
    if (kind) params.set('kind', kind)
    const query = params.toString()
    return request(`/api/team/${slug}/games/${query ? `?${query}` : ''}`)
  },

  /**
   * Одна командная игра (GET /api/team/{slug}/games/{id}/): те же поля, что в
   * истории, плюс `items` — все игры сессии по порядку (у раунда `null`).
   */
  getGame: (slug, id) => request(`/api/team/${slug}/games/${id}/`)
}

export default teamApi
